import { ChessTermType } from "@chesshotel/types";
import { Chess, swapColor, } from "@chesshotel/chess";
import { pieceValues, exchangeEvaluation, uci2move, move2uci, } from "../util.js";
import { checkDefenceLossFork, checkDefendable } from "./util.js";
function getThreats(fen, threatenedColor) {
    const chess = new Chess(fen);
    const seenToSquares = new Set();
    const captures = chess.captures();
    const threats = [];
    for (let i = 0; i < captures.length; i++) {
        const moveStr = captures[i];
        const move = uci2move(moveStr);
        if (seenToSquares.has(move.to))
            continue;
        seenToSquares.add(move.to);
        const evaluation = exchangeEvaluation(fen, move.to);
        if (evaluation.gain <= 0)
            continue;
        const firstSeqMove = evaluation.moveSequence?.[0];
        if (firstSeqMove &&
            (firstSeqMove.to !== move.to || firstSeqMove.from !== move.from)) {
            threats.push({ move: firstSeqMove, evaluation });
        }
        else {
            threats.push({ move, evaluation });
        }
    }
    return threats;
}
function getThreatsFromSquareChess(chess, fromSquare, threatenedColor) {
    const movesFrom = chess.moves({
        square: fromSquare,
        verbose: true,
    });
    const threats = [];
    const seenToSquares = new Set();
    for (let i = 0; i < movesFrom.length; i++) {
        const m = movesFrom[i];
        if (!m.captured)
            continue;
        const move = {
            from: m.from,
            to: m.to,
            promotion: m.promotion,
        };
        if (seenToSquares.has(move.to))
            continue;
        seenToSquares.add(move.to);
        const evaluation = exchangeEvaluation(chess.fen(), move.to, {
            from: fromSquare,
        });
        if (evaluation.gain <= 0)
            continue;
        const firstSeqMove = evaluation.moveSequence?.[0];
        if (firstSeqMove &&
            (firstSeqMove.to !== move.to || firstSeqMove.from !== move.from)) {
            threats.push({ move: firstSeqMove, evaluation });
        }
        else {
            threats.push({ move, evaluation });
        }
    }
    return threats;
}
export function detectFork(fen, moveStr) {
    let chess = new Chess(fen);
    const color = chess.turn();
    const opponentColor = swapColor(color);
    const moveObj = uci2move(moveStr);
    const move = chess.move(moveObj);
    const forkFen = chess.fen();
    let fork = null;
    chess.changeTurn();
    const threatsMovedPiece = getThreatsFromSquareChess(chess, move.to, opponentColor);
    if (threatsMovedPiece.length < 2) {
        return null;
    }
    const threatsBeforeMove = getThreats(fen, opponentColor).filter((threat) => threat.move.from !== moveObj.from);
    const legalMovesByFrom = new Map();
    const isValidMoveCached = (candidate) => {
        let legalSet = legalMovesByFrom.get(candidate.from);
        if (!legalSet) {
            legalSet = new Set(chess.uciMoves({ square: candidate.from }));
            legalMovesByFrom.set(candidate.from, legalSet);
        }
        return legalSet.has(move2uci(candidate));
    };
    const threatsBeforeValid = threatsBeforeMove.filter((threat) => isValidMoveCached(threat.move));
    const threats = threatsMovedPiece.concat(threatsBeforeValid);
    if (threats.length < 2) {
        return null;
    }
    chess.changeTurn();
    let inCheckSquare = null;
    if (chess.inCheck()) {
        inCheckSquare = chess.getKing(opponentColor);
    }
    const forkCaptures = threats.map((threat) => threat.move);
    const exEval = exchangeEvaluation(forkFen, move.to);
    const isCaptureOrTrade = exEval.gain > 0 || (exEval.gain == 0 && exEval.moveSequence.length > 0);
    if (isCaptureOrTrade) {
        let minGainThreshold = 0;
        for (let i = 0; i < forkCaptures.length; i++) {
            const capture = forkCaptures[i];
            const piece = chess.get(capture.to);
            if (!piece)
                continue;
            const val = pieceValues[piece.type];
            if (val > minGainThreshold)
                minGainThreshold = val;
        }
        const defenceMove = exEval.moveSequence[0];
        const defenceMoveStr = move2uci(defenceMove);
        const isDefenceLoss = checkDefenceLossFork(forkFen, defenceMoveStr, minGainThreshold);
        if (!isDefenceLoss) {
            return null;
        }
    }
    const { isDefendable } = checkDefendable(forkFen, forkCaptures, true);
    if (isDefendable) {
        return null;
    }
    chess.changeTurn();
    const attackerFenForExchangeEval = chess.fen();
    let captureMoves = forkCaptures
        .map((move) => {
        if (move.to == inCheckSquare) {
            const checkMove = {
                from: move.from,
                to: inCheckSquare,
            };
            return { ...checkMove, evaluation: { gain: 100, moveSequence: [] } };
        }
        const evaluation = exchangeEvaluation(attackerFenForExchangeEval, move.to, {
            from: move.from,
            backtrackBadMoves: true,
        });
        return { ...move, evaluation };
    })
        .filter(({ evaluation }) => evaluation.gain > 0)
        .sort((a, b) => b.evaluation.gain - a.evaluation.gain);
    if (captureMoves.length >= 2) {
        const move1 = captureMoves[0];
        const move2 = captureMoves[1];
        if (move1.evaluation.gain > 0 || move2.evaluation.gain > 0) {
            const maxGain = Math.min(move1.evaluation.gain, move2.evaluation.gain);
            const preForkChess = new Chess(fen);
            const fromSquare = move.from;
            const preForkToSquares = new Set();
            const fromMoves = preForkChess.uciMoves({
                square: fromSquare,
            });
            for (let i = 0; i < fromMoves.length; i++) {
                preForkToSquares.add(fromMoves[i].slice(2, 4));
            }
            let couldCaptureBeforeFork = false;
            for (let i = 0; i < forkCaptures.length; i++) {
                const to = forkCaptures[i].to;
                if (!preForkToSquares.has(to))
                    continue;
                const evalBefore = exchangeEvaluation(fen, to, {
                    from: fromSquare,
                });
                if (evalBefore.gain >= maxGain) {
                    couldCaptureBeforeFork = true;
                    break;
                }
            }
            if (couldCaptureBeforeFork) {
                return null;
            }
            const term = move1.from == move2.from
                ? ChessTermType.FORK
                : ChessTermType.DOUBLE_ATTACK;
            const arrows = captureMoves
                .map((move) => ({
                from: move.from,
                to: move.to,
            }))
                .filter((move, index) => {
                if (term == ChessTermType.FORK) {
                    return move.from == move1.from;
                }
                else {
                    return index < 2;
                }
            });
            fork = {
                term,
                move,
                maxGain,
                arrows,
                followMoves: [move2uci(move1), move2uci(move2)],
            };
        }
    }
    return fork;
}
//# sourceMappingURL=fork.js.map